import type { PublicReview, ReviewSort } from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import {
  ReviewCard,
  StarRating,
  useProductReviewSummaryQuery,
  useProductReviewsQuery,
} from '@/entities/review';
import { STORE_DETAIL_REVIEWS_ID } from '@/shared/config';
import { Button } from '@/shared/ui';
import { ReviewImageViewer } from './review-image-viewer';
import { ReviewSortSelect } from './review-sort-select';

interface ProductReviewSectionProps {
  readonly productId: string;
}

/** 상품 상세의 후기 영역 — 평균·개수, 정렬·사진 후기 필터, 더 보기, 사진 크게 보기. */
export const ProductReviewSection = ({ productId }: ProductReviewSectionProps): ReactElement => {
  const [sort, setSort] = useState<ReviewSort>('LATEST');
  const [photoOnly, setPhotoOnly] = useState(false);
  const [viewing, setViewing] = useState<{ review: PublicReview; index: number } | null>(null);
  const summary = useProductReviewSummaryQuery(productId);
  const reviews = useProductReviewsQuery({ productId, sort, photoOnly });
  const items = reviews.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    // 위 바로가기 탭이 이 제목으로 내려온다 — 브라우저는 앵커 대상(제목) 자신의 scroll-margin 만 보므로
    // 붙어 있는 헤더에 가리지 않게 띄우는 여백(scroll-mt-28)은 section 이 아니라 제목에 둔다.
    <section aria-labelledby={STORE_DETAIL_REVIEWS_ID} className="mt-24">
      <h2
        id={STORE_DETAIL_REVIEWS_ID}
        className="scroll-mt-28 border-b border-foreground pb-4 text-2xl leading-tight font-bold tracking-[-0.02em]"
      >
        후기
      </h2>
      <div className="mx-auto max-w-4xl">
        {summary.data !== undefined && summary.data.averageRating !== null && (
          <div className="mt-6 flex items-center gap-2">
            <StarRating value={summary.data.averageRating} />
            <span className="text-xl font-bold tabular-nums">
              {summary.data.averageRating.toFixed(1)}
            </span>
            <span className="text-muted-foreground tabular-nums">({summary.data.count})</span>
          </div>
        )}
        <div className="mt-6 flex items-center justify-between gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={photoOnly}
              onChange={(event) => setPhotoOnly(event.target.checked)}
            />
            사진 후기만
          </label>
          <ReviewSortSelect value={sort} onChange={setSort} />
        </div>
        {reviews.isPending && (
          <p role="status" className="py-10 text-center text-sm text-muted-foreground">
            후기를 불러오는 중입니다
          </p>
        )}
        {reviews.isError && (
          <div role="alert" className="py-10 text-center text-sm">
            후기를 불러오지 못했습니다.{' '}
            <Button variant="link" onClick={() => void reviews.refetch()}>
              다시 시도
            </Button>
          </div>
        )}
        {reviews.isSuccess && items.length === 0 && (
          <p className="py-10 text-center text-sm text-muted-foreground">
            {photoOnly ? '사진 후기가 없습니다' : '아직 후기가 없습니다'}
          </p>
        )}
        {items.map((review) => (
          <ReviewCard
            key={review.id}
            review={review}
            onOpenImage={(index) => setViewing({ review, index })}
          />
        ))}
        {reviews.hasNextPage && (
          <div className="mt-6 flex justify-center">
            <Button
              variant="outline"
              disabled={reviews.isFetchingNextPage}
              onClick={() => void reviews.fetchNextPage()}
            >
              후기 더 보기
            </Button>
          </div>
        )}
      </div>
      {viewing !== null && (
        <ReviewImageViewer
          images={viewing.review.images}
          index={viewing.index}
          onChangeIndex={(index) => setViewing({ review: viewing.review, index })}
          onClose={() => setViewing(null)}
        />
      )}
    </section>
  );
};
